import React, { useEffect, useState } from "react";
import { LANDING } from "@/constants/testIds";
import { Plane, Lock, Globe, Archive } from "lucide-react";

const HERO_IMG =
  "https://customer-assets.emergentagent.com/job_members-first/artifacts/s2tqvi9w_33F17E69-A811-4982-96C7-B29B819FB7CD.png";

export default function Hero({ onJoin, waitlistCount }) {
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    const img = new Image();
    img.onload = () => setLoaded(true);
    img.src = HERO_IMG;
  }, []);

  return (
    <section
      data-testid={LANDING.hero}
      className="relative min-h-[100svh] w-full overflow-hidden bg-[hsl(var(--scrm-bg))]"
    >
      {/* Background image */}
      <div className="absolute inset-0">
        <div
          className={`absolute inset-0 bg-cover bg-center scrm-zoom transition-opacity duration-[1400ms] ${
            loaded ? "opacity-100" : "opacity-0"
          }`}
          style={{ backgroundImage: `url(${HERO_IMG})` }}
        />
        <div className="absolute inset-0 hero-overlay" />
      </div>

      {/* Top hairline tag (pushed below fixed nav) */}
      <div className="relative z-10 flex items-center justify-between px-6 md:px-10 pt-20 md:pt-24">
        <span className="text-[10px] md:text-[11px] tracking-eyebrow text-[hsl(var(--scrm-cream))]/70 uppercase font-body">
          A Private Members Club
        </span>
        <span className="hidden md:inline-flex text-[10px] md:text-[11px] tracking-eyebrow text-[hsl(var(--scrm-cream))]/70 uppercase font-body">
          Drop 001 / Live Now
        </span>
      </div>

      {/* Center / lower content */}
      <div className="relative z-10 flex flex-col justify-end min-h-[calc(100svh-160px)] px-6 md:px-10 pb-12 md:pb-16">
        {/* Wordmark */}
        <div className="scrm-rise">
          <div className="flex items-baseline gap-3 md:gap-5 mb-3 md:mb-4">
            <span className="text-[10px] tracking-eyebrow uppercase text-[hsl(var(--scrm-cream))]/60 font-body">
              ✈ SCRM / CLUB
            </span>
            <span className="hairline h-px w-12 md:w-24 bg-[hsl(var(--scrm-cream))]/30" />
          </div>
          <h1
            data-testid={LANDING.heroWordmark}
            className="font-display tracking-mega text-[hsl(var(--scrm-cream))] leading-[0.84]
                       text-[20vw] md:text-[15vw] lg:text-[13vw] xl:text-[200px]"
          >
            SCRM<span className="text-[hsl(var(--scrm-cream))]/40"> </span>CLUB
          </h1>
        </div>

        {/* Headline + sub */}
        <div className="scrm-rise scrm-rise-2 mt-6 md:mt-8 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div className="max-w-2xl">
            <h2
              data-testid={LANDING.heroHeadline}
              className="font-display uppercase tracking-mega text-[hsl(var(--scrm-cream))]
                         text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-[0.95]"
            >
              Rare moments<br className="hidden sm:block" /> only.
            </h2>
            <p
              data-testid={LANDING.heroSubheadline}
              className="mt-5 md:mt-6 font-body font-light text-sm md:text-base text-[hsl(var(--scrm-cream))]/75 max-w-md"
            >
              Limited drops. No restocks. Members get first access. A private club for
              those who collect the moments others miss.
            </p>
          </div>

          <div className="flex flex-col items-start md:items-end gap-4">
            <button
              data-testid={LANDING.heroJoinBtn}
              onClick={onJoin}
              className="btn-cream font-body uppercase tracking-[0.22em] text-xs md:text-[13px] px-8 md:px-10 py-4 md:py-5"
            >
              Join the Waitlist →
            </button>
            <div className="text-[10px] tracking-eyebrow uppercase text-[hsl(var(--scrm-cream))]/55 font-body">
              {waitlistCount !== null
                ? `${waitlistCount.toLocaleString()} on the list`
                : "Application based access"}
            </div>
          </div>
        </div>

        {/* Pillar strip */}
        <div className="scrm-rise scrm-rise-3 mt-12 md:mt-16 border-t hairline pt-8 grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-10">
          {[
            { Icon: Lock, label: "Members Only", sub: "Limited access drops." },
            { Icon: Globe, label: "Global Community", sub: "A worldwide private circle." },
            { Icon: Plane, label: "Limited Drops", sub: "Small batches. No restocks." },
            { Icon: Archive, label: "The Archive", sub: "Once it's gone, it's archived." },
          ].map(({ Icon, label, sub }, i) => (
            <div key={label} className="flex flex-col gap-2">
              <Icon className="h-4 w-4 text-[hsl(var(--scrm-cream))]/80" strokeWidth={1.25} />
              <div className="font-display uppercase text-xs md:text-sm tracking-[0.18em] text-[hsl(var(--scrm-cream))]">
                {label}
              </div>
              <div className="font-body text-[11px] md:text-xs text-[hsl(var(--scrm-cream))]/55 leading-relaxed">
                {sub}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
